import { useRef, useState } from "react";
import ReactCrop, {
  centerCrop,
  Crop,
  makeAspectCrop,
  PixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { CyItem } from "./CyItem";

interface Props {
  src: string;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
}

export function ImageCropModal({ src, isOpen, onCancel, onConfirm }: Props) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement>(null);
  /**
   * Kaum sichtbarer Qualitätsverlust
   * Dateigröße ~50% kleiner als bei 1.0
   * Auch nach mehrmaligem Bearbeiten akzeptabel
   */
  const quality = 0.95;

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        width / height, // You might want to allow different aspects or none
        width,
        height,
      ),
      width,
      height,
    );

    setCrop(crop);
  }

  const handleConfirm = async () => {
    if (!completedCrop || !imgRef.current) {
      return;
    }

    const canvas = document.createElement("canvas");
    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return;
    }

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
    );

    canvas.toBlob(
      (blob) => {
        if (blob) {
          onConfirm(blob);
        }
      },
      "image/webp",
      quality,
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onCancel}
      className="modal-xl modal-fullscreen-xl-down"
    >
      <ModalHeader toggle={onCancel}>Bild zuschneiden</ModalHeader>
      <ModalBody className="d-flex justify-content-center bg-light p-4">
        <ReactCrop
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
        >
          <img
            ref={imgRef}
            alt="Crop me"
            src={src}
            onLoad={onImageLoad}
            style={{ maxWidth: "100%", maxHeight: "65vh" }}
            crossOrigin="anonymous"
          />
        </ReactCrop>
      </ModalBody>
      <ModalFooter className="justify-content-end">
        <div className="d-flex gap-2">
          <Button
            color="secondary"
            onClick={onCancel}
            data-cy={CyItem.CropModalCancelButton}
          >
            Abbrechen
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            disabled={!completedCrop}
            data-cy={CyItem.CropModalConfirmButton}
          >
            Zuschneiden & Speichern
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
